/*
 * Vereinheitlichter Widerruf-/Rücksende-Step.
 * - Bestellung noch nicht versendet -> direkter Widerruf (Storno).
 * - Bestellung bereits versendet    -> Widerruf mit Rückversand; standardmäßig Teilauswahl
 *   (Switch "gesamte Bestellung" für Ein-Klick-Komplettwiderruf), Grund und Bilder freiwillig.
 */
import {
    Alert,
    Backdrop,
    Card,
    CardContent,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    MenuItem,
    Select,
    Switch,
    Tooltip
} from "@mui/material";
import SoftButton from "../components/SoftButton";
import SoftTypography from "../components/SoftTypography";
import Grid2 from '@mui/material/Unstable_Grid2';
import {useState} from "react";
import SoftInput from "../components/SoftInput";
import Separator from "../components/Separator";
import {Warning} from "@mui/icons-material";
import DropzoneArea from "./nested/Dropzone";
import RetourenAPI from "../api/RetourenAPI";
import SoftBox from "../components/SoftBox";
import {Vortex} from "react-loader-spinner";

const reasons = [
    'Es hat mir nicht gefallen',
    'Es passt mir nicht',
    'Die Farbe ist nicht wie angegeben',
    'Das Produkt ist beschädigt',
    'Ich möchte keinen Grund angeben'
];

export default function ChooseProduct(props) {
    const order = props.order;
    const token = props.token;
    const data = props.data;
    const onSubmitted = props.onSubmitted;

    const shipped = order?.informations?.shipped === true;
    const withdrawalExpired = order?.informations?.withdrawalExpired === true;
    // Artikel, die NICHT über Brandsky laufen -> wickelt der Händler direkt ab, nicht an uns senden.
    const externalItems = [...new Set((order?.products ?? []).filter(p => p.disabled).map(p => p.name))];

    const [wholeOrder, setWholeOrder] = useState(false);
    const [checkedProducts, setCheckedProducts] = useState([]);
    const [reason, setReason] = useState(undefined);
    const [remarks, setRemarks] = useState(undefined);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);

    const toBase64 = file => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
    });

    const encodeText = (text) => {
        try {
            return btoa(unescape(encodeURIComponent(text)));
        } catch (e) {
            return null;
        }
    };

    const canSubmit = () => {
        if (!shipped) return true;
        if (wholeOrder) return true;
        return checkedProducts.length > 0;
    };

    const submit = async () => {
        setLoading(true);

        let productsJSONArray = null;
        let cause = null;
        let remarksB64 = null;
        let imagesB64JSONArray = null;

        if (shipped) {
            if (!wholeOrder) {
                productsJSONArray = JSON.stringify(checkedProducts.map(p => ({
                    name: p.name,
                    variant: p.variant,
                    shop_variant: p.shop_variant,
                })));
            }
            if (reason && reason !== '') cause = reason;
            if (remarks && remarks.trim() !== '') remarksB64 = encodeText(remarks.trim());
            if (files.length > 0) {
                const filesArray = [];
                for (const f of files) {
                    filesArray.push(await toBase64(f));
                }
                imagesB64JSONArray = JSON.stringify(filesArray);
            }
        }

        RetourenAPI.createWiderruf(token, {
            orderId: order.orderId,
            zip: order.plz,
            email: order.email,
            productsJSONArray,
            cause,
            remarksB64,
            imagesB64JSONArray,
        }).then(res => {
            onSubmitted(res.data?.status ?? 'RECEIVED');
        }).catch(() => {
            onSubmitted('RECEIVED');
        }).finally(() => setLoading(false));
    };

    const loadingScene = () => (
        <SoftBox display="flex" justifyContent="center">
            <div className='pulse'>
                <Vortex visible={true} height="80" width="80" ariaLabel="vortex-loading"
                        colors={['#ffd6ff', '#e7c6ff', '#c8b6ff', '#b8c0ff', '#bbd0ff', '#cae9ff']}/>
            </div>
        </SoftBox>
    );

    const toggleProduct = (product, index) => {
        if (product.disabled === true) return;
        if (checkedProducts.some(p => p.index === index)) {
            setCheckedProducts(checkedProducts.filter(p => p.index !== index));
        } else {
            setCheckedProducts(prev => [...prev, {...product, index}]);
        }
    };

    return (
        <>
            <img className='logoImg' src={data?.logo} height={50} alt=""/><br/>
            <Card sx={{minHeight: '60vh', maxWidth: 750, minWidth: 350}} className='glassMorphCard'>
                <CardContent>
                    {loading ? (
                        <Backdrop sx={{color: '#fff', zIndex: (t) => t.zIndex.drawer + 1}} open={loading}>
                            {loadingScene()}
                        </Backdrop>
                    ) : (
                        <Grid2 container direction='row' justifyContent='center' textAlign='center' spacing={2}>
                            <Grid2 item xs={12}>
                                <SoftTypography fontWeight='bold' variant='h3'>Bestellung #{order?.orderId}</SoftTypography>
                            </Grid2>

                            {!shipped ? (
                                <>
                                    <Grid2 item xs={12}>
                                        <SoftTypography variant='body2' color='text'>
                                            Deine Bestellung wurde noch nicht versendet. Du kannst die
                                            <strong> gesamte Bestellung</strong> direkt widerrufen – ein Rückversand
                                            ist nicht nötig.
                                        </SoftTypography>
                                    </Grid2>
                                    {order?.products?.map((product, index) => (
                                        <Grid2 item xs={12} key={index}>
                                            <Card sx={{opacity: product.disabled ? 0.7 : 1}}>
                                                <Grid2 container xs={12} direction='row' lineHeight='70px'>
                                                    <Grid2 item xs={2} lg={1}>
                                                        <div style={{
                                                            position: 'relative', height: '90%', width: '100%',
                                                            background: "#fff url(" + product.img + ") center center/contain no-repeat",
                                                            top: '5%', borderRadius: '15px'
                                                        }}>&nbsp;</div>
                                                    </Grid2>
                                                    <Grid2 item xs={8} textAlign='left' sx={{mt: 0.5, ml: 2}}>
                                                        <SoftTypography variant='body1'>{product.name}</SoftTypography>
                                                        <SoftTypography variant='body2'>Menge: {product.amount}</SoftTypography>
                                                    </Grid2>
                                                </Grid2>
                                            </Card>
                                        </Grid2>
                                    ))}
                                    <Grid2 item xs={12} md={8}>
                                        <SoftButton color='primary' variant='gradient' fullWidth
                                                    disabled={loading}
                                                    onClick={async () => await submit()}>
                                            Vertrag widerrufen
                                        </SoftButton>
                                    </Grid2>
                                </>
                            ) : withdrawalExpired ? (
                                <Grid2 item xs={12}>
                                    <SoftTypography variant='h5' fontWeight='bold' mb={1}>
                                        Widerruf nicht mehr möglich
                                    </SoftTypography>
                                    <SoftTypography variant='body2' color='text'>
                                        Die gesetzliche Widerrufsfrist ab Zustellung deiner Bestellung ist bereits
                                        abgelaufen. Ein Widerruf über dieses Portal ist daher nicht mehr möglich.
                                    </SoftTypography>
                                </Grid2>
                            ) : (
                                <>
                                    <Grid2 item xs={12}>
                                        <SoftTypography variant='body2' color='text'>
                                            Ein Widerruf hat einen Rückversand zur Folge. Die Kosten der
                                            Rücksendung trägst du. Wir bestätigen dir den Eingang per E-Mail.
                                        </SoftTypography>
                                    </Grid2>
                                    {externalItems.length > 0 && (
                                        <Grid2 item xs={12}>
                                            <Alert severity="info">
                                                Folgende Artikel werden <strong>nicht über dieses Portal</strong> abgewickelt
                                                und dürfen <strong>nicht an uns gesendet</strong> werden:&nbsp;
                                                <strong>{externalItems.join(', ')}</strong>. Für diese wickelt der Händler
                                                den Widerruf direkt mit dir ab und meldet sich bei dir.
                                            </Alert>
                                        </Grid2>
                                    )}
                                    <Grid2 item xs={12}>
                                        <FormControlLabel
                                            control={<Switch checked={wholeOrder}
                                                             onChange={(e) => setWholeOrder(e.target.checked)}/>}
                                            label="Ich will die gesamte Bestellung widerrufen"
                                        />
                                    </Grid2>

                                    {!wholeOrder && <>
                                        <Grid2 item xs={12}>
                                            <Divider orientation='horizontal'/>
                                            <SoftTypography variant='subtitle2'>Welche Produkte möchtest du
                                                widerrufen?</SoftTypography>
                                        </Grid2>
                                        {order?.products?.map((product, index) => (
                                            <Grid2 item xs={12} key={index}>
                                                <Card sx={{
                                                    cursor: 'pointer',
                                                    boxShadow: checkedProducts.some(p => p.index === index) ? '0 0 0 2px purple' : ''
                                                }} onClick={() => toggleProduct(product, index)}>
                                                    <Grid2 container xs={12} direction='row' lineHeight='70px'>
                                                        <Grid2 item xs={2} lg={1}>
                                                            {product.disabled ?
                                                                <Tooltip title={"Bitte melde dich direkt bei uns, um dieses Produkt zu widerrufen."}>
                                                                    <Warning color={'warning'}/>
                                                                </Tooltip>
                                                                :
                                                                <Checkbox size='large' color='secondary'
                                                                          checked={checkedProducts.some(p => p.index === index)}/>
                                                            }
                                                        </Grid2>
                                                        <Grid2 item xs={2} lg={1}>
                                                            <div style={{
                                                                position: 'relative', height: '90%', width: '100%',
                                                                background: "#fff url(" + product.img + ") center center/contain no-repeat",
                                                                top: '5%', borderRadius: '15px'
                                                            }}>&nbsp;</div>
                                                        </Grid2>
                                                        <Grid2 item xs={5} textAlign='left' sx={{mt: 0.5, ml: 2}}>
                                                            <SoftTypography variant='body1'
                                                                            color={product.disabled ? 'text' : 'primary'}>{product.name}</SoftTypography>
                                                            <SoftTypography variant='body2'
                                                                            color={product.disabled ? 'text' : ''}>Menge: {product.amount}</SoftTypography>
                                                        </Grid2>
                                                    </Grid2>
                                                </Card>
                                            </Grid2>
                                        ))}
                                    </>}

                                    <Grid2 item xs={12}>
                                        <Separator text='Optional'/>
                                    </Grid2>
                                    <Grid2 item xs={12} textAlign='left'>
                                        <FormControl fullWidth>
                                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                Grund (freiwillig)
                                            </SoftTypography>
                                            <Select displayEmpty value={reason ?? ''}
                                                    onChange={(e) => setReason(e.target.value)}
                                                    input={<SoftInput/>}
                                                    renderValue={(selected) => selected && selected !== '' ? selected :
                                                        <em>Optional einen Grund angeben</em>}>
                                                <MenuItem value=""><em>Kein Grund</em></MenuItem>
                                                {reasons.map((r) => (<MenuItem key={r} value={r}>{r}</MenuItem>))}
                                            </Select>
                                        </FormControl>
                                    </Grid2>
                                    <Grid2 item xs={12} textAlign='left'>
                                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                                            Bemerkung (freiwillig)
                                        </SoftTypography>
                                        <SoftInput multiline value={remarks ?? ''}
                                                   onChange={(e) => setRemarks(e.target.value)}/>
                                    </Grid2>
                                    <Grid2 item xs={12} textAlign='left'>
                                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                                            Bilder anhängen (freiwillig)
                                        </SoftTypography>
                                        <DropzoneArea files={files} setFiles={setFiles}/>
                                    </Grid2>

                                    <Grid2 item xs={12} md={8}>
                                        <SoftButton color={canSubmit() ? 'primary' : 'secondary'} variant='gradient'
                                                    fullWidth disabled={loading || !canSubmit()}
                                                    onClick={async () => await submit()}>
                                            Widerruf bestätigen
                                        </SoftButton>
                                    </Grid2>
                                </>
                            )}
                        </Grid2>
                    )}
                </CardContent>
            </Card>
        </>
    );
}
